import * as React from 'react';
import classnames from 'classnames';
import { Tooltip } from '@ixicare/ui';
import { t } from '~i18n';
import { Field, FieldValue } from '../schema';
import styles from '../styles.module.css';

export interface DifferingKeyDetail {
  key: string;
  values: { mac: string; value: FieldValue }[];
}

const formatValue = (value: FieldValue): string => (value == null ? '–' : String(value));

const UNIT_LABEL: Record<string, string> = {
  seconds: '(s)',
  milliseconds: '(ms)',
  meters: '(m)',
  percent: '(%)',
  milli_g: '(milli-g)',
  meters_per_second_squared: '(m/s²)',
  g: '(g)',
  bits: '(bits)',
  hertz: '(Hz)',
};

export const formatUnit = (unit?: string) => {
  if (!unit) return '';
  return UNIT_LABEL[unit] ?? `(${unit})`;
};

export const labelWithUnit = (title: string, unit?: string): string => {
  const label = t(title);
  const unitLabel = formatUnit(unit);
  return unitLabel ? `${label} ${unitLabel}` : label;
};

interface Props {
  field: Field;
  value?: FieldValue;
  error?: string;
  changed?: boolean;
  bulk?: boolean;
  differing?: boolean;
  differingDetail?: DifferingKeyDetail[];
  children: React.ReactNode;
}

const differingTooltip = (detail: DifferingKeyDetail[]): React.ReactNode => (
  <div className={styles.tooltipDiffering}>
    {detail.map((d) => (
      <div key={d.key} className={styles.tooltipDifferingGroup}>
        {detail.length > 1 && <div className={styles.tooltipDifferingKey}>{d.key}</div>}
        {d.values.map((v) => (
          <div key={v.mac} className={styles.tooltipDifferingRow}>
            <span className={styles.tooltipDifferingMac}>{v.mac}</span>
            <span>{formatValue(v.value)}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
);

const FieldWrapper: React.FC<Props> = ({
  field,
  value,
  error,
  changed,
  bulk,
  differing,
  differingDetail,
  children,
}) => {
  const differsFromSite = field.site_config_value !== undefined && value !== field.site_config_value;
  const isChanged = Boolean(field.highlight || changed);
  const isSite = differsFromSite;

  const cls = classnames(
    styles.field,
    field.span === 2 && styles.span2,
    field.span === 3 && styles.span3,
    field.span === 4 && styles.span4,
    field.enabled === false && styles.disabled,
    isChanged && !isSite && styles.flagChanged,
    isSite && !isChanged && styles.flagSite,
    isChanged && isSite && styles.flagBoth,
    bulk && differing && styles.flagDiffering
  );

  return (
    <div className={cls} id={field.id}>
      <span className={styles.fieldFlags}>
        {differsFromSite && (
          <span className={styles.siteConfigOverride}>
            ● {t('device-config.common.site-config')}: {String(field.site_config_value)}
          </span>
        )}
        {(field.highlight || changed) && <span className={styles.changedDot}>● changed</span>}
        {bulk &&
          (differing ? (
            differingDetail && differingDetail.length ? (
              <span className={styles.differingTrigger}>
                <Tooltip
                  title={t('device-config.bulk.differingTitle')}
                  content={differingTooltip(differingDetail)}
                  overrideWidth={260}
                >
                  <span className={styles.differingDot}>● {t('device-config.bulk.differs')}</span>
                </Tooltip>
              </span>
            ) : (
              <span className={styles.differingDot}>● {t('device-config.bulk.differs')}</span>
            )
          ) : (
            <span className={styles.commonDot}>● {t('device-config.bulk.common')}</span>
          ))}
      </span>
      {children}
      {error && <span className={styles.errorMsg}>{error}</span>}
    </div>
  );
};

export default FieldWrapper;
